/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   Workspaces.tsx
 *     Description     :   Workspaces secondary dashboard page
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-27
 *     Modified        :   2026-06-14 | Conformed to the Spec 197 design blueprint —
 *                         migrated off MUI (Container/Grid/Paper/Table/Chip/Tabs/
 *                         IconButton) onto the canonical Tailwind+token UI kit
 *                         (PageLayout / Card / DataTable / StatCard / Button / Input /
 *                         Select / Badge). Presentation only — data fetching, filters,
 *                         sync/enable/disable actions, the entity-detail WorkspaceModal,
 *                         and the WorkspaceForm are all unchanged.
 *     Constitution    :   v1.19.0
 *     Feature         :   workspace-management
 * =========================================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Search,
  AlertTriangle,
  CheckCircle,
  Folder,
  RefreshCcw,
  Play,
  Pause,
  Database,
  Building2,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import WorkspaceForm from '../components/workspaces/WorkspaceForm';
import { WorkspaceModal } from '../../../pages/Workspaces/WorkspaceModal';
import { workspaceHelp } from '../../../data/help/workspaceHelp';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Select,
  Badge,
  StatCard,
  FilterBar,
  ErrorState,
  LoadingState,
  Extended,
  QuickGlance,
  AppTooltip,
  PageHelpPanel,
} from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import { UnmeasuredSizeTag, unmeasuredSizeNote } from '../../../utils/unmeasuredSize';
import { PaginationControls } from '../../../components/operations/PaginationControls';

// ============================================================================
// Types
// ============================================================================

export interface Workspace {
  id: string;
  fabric_workspace_id: string | null;
  name: string;
  type: string | null;
  description: string | null;
  is_enabled: boolean;
  capacity_id: string | null;
  capacity_name: string | null;
  client_name: string | null;
  subscription_id: string | null;
  subscription_name: string | null;
  lakehouse_count: number;
  warehouse_count: number;
  semantic_model_count: number;
  backup_enabled_count: number;
  storage_gb: number;
  /** Lakehouses and warehouses in storage_gb the last collection did not size (counted at their last known size). */
  size_not_measured_count?: number;
  last_sync_at: string | null;
  created_at: string;
  updated_at: string;
}

interface WorkspaceStats {
  total: number;
  enabled: number;
  disabled: number;
  withSemanticModels: number;
  totalLakehouses: number;
  totalWarehouses: number;
  backupEnabledLakehouses: number;
  totalStorageGb: number;
  /** Lakehouses and warehouses in totalStorageGb the last collection did not size. */
  sizeNotMeasuredCount?: number;
  distinctCapacities: number;
}

interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

type EnabledFilter = 'all' | 'enabled' | 'disabled';
type ModelsFilter = 'all' | 'with' | 'without';

interface SubscriptionOption {
  id: string;
  name: string;
}

// ============================================================================
// Component
// ============================================================================

/** One empty list, so memos keyed on the rows stay stable before the first load. */
const NO_WORKSPACES: Workspace[] = [];

const Workspaces: React.FC = () => {
  const { canAccess } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  // The page the table asks for; the totals come back with the list.
  const [pageRequest, setPageRequest] = useState({ page: 1, pageSize: 20 });
  const [syncLoading, setSyncLoading] = useState(false);
  // Sync and enable/disable failures. A failed list load or refresh is the list query's own error.
  const [actionError, setActionError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);

  // Modal state for entity detail view (Feature 116)
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filters — ALL server-side (Spec 216 T017: the tab row + client re-mask are gone)
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [enabledFilter, setEnabledFilter] = useState<EnabledFilter>(
    (searchParams.get('enabled') as EnabledFilter) || 'all'
  );
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>(
    searchParams.get('subscription') || 'all'
  );
  const [modelsFilter, setModelsFilter] = useState<ModelsFilter>(
    (searchParams.get('models') as ModelsFilter) || 'all'
  );
  const [subscriptions, setSubscriptions] = useState<SubscriptionOption[]>([]);

  const canManage = canAccess('admin');

  // Fetch workspaces. Spec 226 (FR-011): React Query keeps the rows of the current filters and page on
  // screen through a refresh, and a failed refresh shows the Refresh badge instead of replacing them.
  const listQuery = useQuery({
    queryKey: ['workspaces', 'list', pageRequest.page, pageRequest.pageSize, searchQuery, enabledFilter, subscriptionFilter, modelsFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', pageRequest.page.toString());
      params.set('pageSize', pageRequest.pageSize.toString());

      if (searchQuery) params.set('search', searchQuery);
      if (enabledFilter !== 'all') params.set('is_enabled', (enabledFilter === 'enabled').toString());
      if (subscriptionFilter !== 'all') params.set('subscription_id', subscriptionFilter);
      if (modelsFilter !== 'all') params.set('has_semantic_models', (modelsFilter === 'with').toString());

      return api.get<{
        success: boolean;
        data: Workspace[];
        pagination: Pagination;
      }>(`/api/v1/workspaces?${params.toString()}`);
    },
  });
  const statsQuery = useQuery({
    queryKey: ['workspaces', 'stats'],
    queryFn: async () => (await api.get<{ success: boolean; data: WorkspaceStats }>('/api/v1/workspaces/stats')).data,
  });
  const workspaces = listQuery.data?.data ?? NO_WORKSPACES;
  const pagination: Pagination = listQuery.data?.pagination ?? { ...pageRequest, total: 0, totalPages: 0 };
  const stats = statsQuery.data ?? null;
  const statsNote = statsQuery.isError ? 'workspace figures could not be read' : 'loading…';
  const loading = listQuery.isLoading;
  const listError = listQuery.data || !listQuery.error
    ? null
    : listQuery.error instanceof Error ? listQuery.error.message : 'Failed to fetch workspaces';
  const error = actionError ?? listError;

  // Reloads after an action; the header's standard Refresh re-fetches both (refreshAll).
  const { refetch: refetchList } = listQuery;
  const { refetch: refetchStats } = statsQuery;
  const fetchWorkspaces = useCallback(async () => { await refetchList(); }, [refetchList]);
  const fetchStats = useCallback(async () => { await refetchStats(); }, [refetchStats]);
  const refreshAll = useCallback(async () => {
    await Promise.all([refetchList({ throwOnError: true }), refetchStats({ throwOnError: true })]);
  }, [refetchList, refetchStats]);

  // Fetch subscription filter options (once)
  useEffect(() => {
    api
      .get<{ success: boolean; data: { subscriptions: SubscriptionOption[] } }>(
        '/api/v1/workspaces/filter-options'
      )
      .then((response) => setSubscriptions(response.data.subscriptions ?? []))
      .catch(() => setSubscriptions([]));
  }, []);

  // Sync from Fabric
  const handleSync = async (): Promise<void> => {
    if (!canManage) return;

    setSyncLoading(true);
    setActionError(null);

    try {
      await api.post('/api/v1/workspaces/sync');
      await Promise.all([fetchWorkspaces(), fetchStats()]);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to sync workspaces');
    } finally {
      setSyncLoading(false);
    }
  };

  // Enable/Disable workspace
  const handleToggleWorkspace = async (workspace: Workspace, enable: boolean): Promise<void> => {
    if (!canManage) return;

    try {
      await api.post(`/api/v1/workspaces/${workspace.id}/${enable ? 'enable' : 'disable'}`);
      await fetchWorkspaces();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : `Failed to ${enable ? 'enable' : 'disable'} workspace`);
    }
  };

  // Pagination handlers (server-side — PaginationControls is the source of truth)
  const handlePageChange = (page: number): void => {
    setPageRequest((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (size: number): void => {
    setPageRequest({ pageSize: size, page: 1 });
  };

  // Reset to page 1 whenever a filter changes so we never land on an
  // out-of-range page of a smaller filtered result set.
  useEffect(() => {
    setPageRequest((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [searchQuery, enabledFilter, subscriptionFilter, modelsFilter]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (enabledFilter !== 'all') params.set('enabled', enabledFilter);
    if (subscriptionFilter !== 'all') params.set('subscription', subscriptionFilter);
    if (modelsFilter !== 'all') params.set('models', modelsFilter);
    setSearchParams(params);
  }, [searchQuery, enabledFilter, subscriptionFilter, modelsFilter, setSearchParams]);

  // Summary stats (Spec 216 T017: meaningful tiles — active %, with-models,
  // storage GB, capacity spread)
  // Spec 298 FR-005: no summary until the stats read answers (the tiles say loading or could not be read, never 0);
  // FR-004: the active share over no workspaces is null, and the tile says so.
  const summaryStats = useMemo(() => {
    if (!stats) return null;
    const total = stats.total || 0;
    const enabled = stats.enabled || 0;
    return {
      total,
      enabled,
      activePct: total > 0 ? Math.round((enabled / total) * 100) : null,
      withModels: stats.withSemanticModels || 0,
      storageGb: stats.totalStorageGb || 0,
      sizeNotMeasured: stats.sizeNotMeasuredCount || 0,
      capacities: stats.distinctCapacities || 0,
      totalLakehouses: stats.totalLakehouses || 0,
      totalWarehouses: stats.totalWarehouses || 0,
    };
  }, [stats]);

  // Handle form close
  const handleFormClose = (): void => {
    setShowForm(false);
    setSelectedWorkspace(null);
  };

  // Handle workspace saved
  const handleWorkspaceSaved = async (): Promise<void> => {
    handleFormClose();
    await Promise.all([fetchWorkspaces(), fetchStats()]);
  };

  // Format storage (GB in, GB/TB out) for the per-row Storage column
  const formatStorage = (gb: number | null | undefined): string => {
    if (!gb || gb <= 0) return '—';
    if (gb >= 1024) return `${(gb / 1024).toFixed(2)} TB`;
    if (gb >= 10) return `${gb.toFixed(1)} GB`;
    return `${gb.toFixed(2)} GB`;
  };

  // Compact clickable item count (LH/WH) for the Items column
  const itemCount = (count: number, label: string, to: string): React.ReactNode =>
    count > 0 ? (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          navigate(to);
        }}
        className="font-medium text-[var(--color-accent)] hover:underline"
        title={`View ${count} ${label} in this workspace`}
      >
        {count}
        <span className="ml-0.5 font-normal text-[var(--color-text-secondary)]">
          {label}
        </span>
      </button>
    ) : (
      <span className="text-[var(--color-text-secondary)]">
        0 {label}
      </span>
    );

  // Table columns — management-focused (who it serves, footprint, protection).
  const columns: DataTableColumn<Workspace>[] = [
    {
      key: 'name',
      header: 'Workspace',
      width: '26%',
      render: (workspace) => (
        <div className="flex min-w-0 items-center gap-2">
          <div className={iconTileClass(workspace.is_enabled)}>
            <Folder
              className={`h-4 w-4 ${
                workspace.is_enabled
                  ? 'text-[var(--color-accent)]'
                  : 'text-[var(--color-text-secondary)]'
              }`}
            />
          </div>
          <div className="min-w-0">
            <div className="truncate font-medium text-[var(--color-text-primary)]" title={workspace.name}>
              {workspace.name}
            </div>
            <div className="truncate text-xs text-[var(--color-text-secondary)]">
              {workspace.type || 'Workspace'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'capacity_name',
      header: 'Capacity',
      width: '13%',
      render: (workspace) =>
        workspace.capacity_name ? (
          <span className="truncate text-[var(--color-text-primary)]" title={workspace.subscription_name ?? undefined}>
            {workspace.capacity_name}
          </span>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ),
    },
    {
      key: 'client_name',
      header: 'Client',
      width: '14%',
      render: (workspace) =>
        workspace.client_name ? (
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <Building2 className="h-3.5 w-3.5 flex-shrink-0 text-[var(--color-text-secondary)]" />
            <span className="truncate text-[var(--color-text-primary)]" title={workspace.client_name}>
              {workspace.client_name}
            </span>
          </span>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ),
    },
    {
      key: 'storage_gb',
      header: 'Storage',
      width: '9%',
      align: 'right',
      render: (workspace) => (
        <span className="text-[var(--color-text-primary)]">
          {formatStorage(workspace.storage_gb)}
          {/* Spec 286 T159 item 32: the sum counts items the last collection did not size at their last known size. */}
          <UnmeasuredSizeTag count={workspace.size_not_measured_count} />
        </span>
      ),
    },
    {
      key: 'items',
      header: 'Items',
      width: '15%',
      sortable: false,
      render: (workspace) => (
        <div className="flex items-center gap-1.5 whitespace-nowrap text-sm">
          {itemCount(workspace.lakehouse_count, 'LH', `/lakehouses?workspace_id=${workspace.id}`)}
          <span className="text-[var(--color-text-secondary)]">·</span>
          {itemCount(workspace.warehouse_count, 'WH', `/warehouses?workspace_id=${workspace.id}`)}
          <span className="text-[var(--color-text-secondary)]">·</span>
          <span className="text-[var(--color-text-secondary)]">
            {workspace.semantic_model_count} SM
          </span>
        </div>
      ),
    },
    {
      key: 'backup_enabled_count',
      header: 'Backup coverage',
      width: '11%',
      render: (workspace) =>
        workspace.lakehouse_count > 0 ? (
          <div className="flex items-center gap-1.5">
            <span className="text-[var(--color-text-primary)]">
              {workspace.backup_enabled_count} / {workspace.lakehouse_count}
            </span>
            {workspace.backup_enabled_count === workspace.lakehouse_count ? (
              <CheckCircle className="h-4 w-4 text-[var(--color-status-success)]" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-[var(--color-status-warning)]" />
            )}
          </div>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ),
    },
    {
      key: 'is_enabled',
      header: 'Status',
      width: '8%',
      render: (workspace) => (
        <AppTooltip
          title={
            workspace.is_enabled
              ? 'Workspace is enabled for backup management'
              : 'Workspace is disabled and not being managed'
          }
        >
          <Badge variant={workspace.is_enabled ? 'success' : 'secondary'}>
            {workspace.is_enabled ? 'Enabled' : 'Disabled'}
          </Badge>
        </AppTooltip>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: '4%',
      render: (workspace) =>
        canManage ? (
          <div className="flex items-center justify-end gap-1">
            <AppTooltip title={workspace.is_enabled ? 'Disable' : 'Enable'}>
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleWorkspace(workspace, !workspace.is_enabled);
                }}
              >
                {workspace.is_enabled ? (
                  <Pause className="h-4 w-4 text-[var(--color-status-warning)]" />
                ) : (
                  <Play className="h-4 w-4 text-[var(--color-status-success)]" />
                )}
              </Button>
            </AppTooltip>
          </div>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ),
    },
  ];

  return (
    <PageLayout
      title="Workspaces"
      description="Manage Microsoft Fabric workspaces and their backup configurations"
      icon={<Folder className="h-6 w-6" />}
      onRefresh={refreshAll}
      isRefreshing={listQuery.isFetching || statsQuery.isFetching}
      lastFetchedAt={listQuery.dataUpdatedAt}
      refreshError={listQuery.error ?? statsQuery.error}
      actions={
        <>
          <PageHelpPanel content={workspaceHelp} buttonSize="sm" />
          {canManage && (
            <AppTooltip title="Synchronize workspace data from Microsoft Fabric">
              <Button
                variant="primary"
                leftIcon={<RefreshCcw className="h-4 w-4" />}
                onClick={handleSync}
                isLoading={syncLoading}
              >
                {syncLoading ? 'Syncing...' : 'Sync from Fabric'}
              </Button>
            </AppTooltip>
          )}
        </>
      }
    >
      {error && !loading ? (
        <ErrorState
          title="Failed to load workspaces"
          message={error}
          onRetry={() => {
            setActionError(null);
            return refreshAll();
          }}
        />
      ) : (
        <>
          {/* Summary tiles (Spec 216 T017: meaningful stats; Spec 298: "—" until the stats read answers) */}
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              label="Workspaces"
              value={summaryStats ? summaryStats.total : '—'}
              subtext={!summaryStats ? statsNote
                : summaryStats.activePct === null ? 'no workspaces' : `${summaryStats.activePct}% active`}
              icon={<Folder className="h-4 w-4" />}
            />
            <StatCard
              label="With Semantic Models"
              value={summaryStats ? summaryStats.withModels : '—'}
              valueClassName="text-[var(--color-accent)]"
              subtext={summaryStats ? `of ${summaryStats.total} workspaces` : statsNote}
              icon={<Database className="h-4 w-4" />}
            />
            <StatCard
              label="Storage"
              value={summaryStats ? `${summaryStats.storageGb.toLocaleString()} GB` : '—'}
              valueClassName="text-[var(--color-status-success)]"
              subtext={!summaryStats ? statsNote : [
                `${summaryStats.totalLakehouses} lakehouses · ${summaryStats.totalWarehouses} warehouses`,
                unmeasuredSizeNote(summaryStats.sizeNotMeasured),
              ].filter(Boolean).join(' · ')}
            />
            <StatCard
              label="Capacity Spread"
              value={summaryStats ? summaryStats.capacities : '—'}
              subtext={summaryStats ? 'distinct capacities in use' : statsNote}
              icon={<CheckCircle className="h-4 w-4" />}
            />
          </div>

          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for the filters and full table.
            </Card>
          </QuickGlance>

          <Extended>
          {/* Filters */}
          <FilterBar
            trailing={
              <span className="text-sm text-[var(--color-text-secondary)]">
                {workspaces.length} of {pagination.total} workspaces
              </span>
            }
          >
            <div className="w-full md:w-72">
              <Input
                isFullWidth
                placeholder="Search workspaces..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                leftAddon={<Search className="h-4 w-4" />}
              />
            </div>
            <Select
              label="Status"
              value={enabledFilter}
              onChange={(e) => setEnabledFilter(e.target.value as EnabledFilter)}
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'enabled', label: 'Enabled' },
                { value: 'disabled', label: 'Disabled' },
              ]}
            />
            <Select
              label="Subscription"
              value={subscriptionFilter}
              onChange={(e) => setSubscriptionFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Subscriptions' },
                ...subscriptions.map((s) => ({ value: s.id, label: s.name })),
              ]}
            />
            <Select
              label="Semantic Models"
              value={modelsFilter}
              onChange={(e) => setModelsFilter(e.target.value as ModelsFilter)}
              options={[
                { value: 'all', label: 'All Workspaces' },
                { value: 'with', label: 'With Models' },
                { value: 'without', label: 'Without Models' },
              ]}
            />
          </FilterBar>

          {/* Table — filtering + pagination are fully server-side (no tab re-mask) */}
          <Card padding="none">
            {loading ? (
              <div className="p-4">
                <LoadingState rows={6} />
              </div>
            ) : (
              <DataTable
                columns={columns}
                rows={workspaces}
                rowKey={(w) => w.id}
                pageSize={pagination.pageSize}
                showPagination={false}
                minWidth="64rem"
                emptyMessage="No workspaces found — sync from Fabric to get started."
                onRowClick={(w) => {
                  setSelectedWorkspaceId(w.id);
                  setIsModalOpen(true);
                }}
              />
            )}
          </Card>

          {/* Standard blueprint pagination — server-driven, all 512 reachable.
              Rendered whenever there are rows so the page-size selector is always
              available (not just when totalPages > 1). */}
          {!loading && pagination.total > 0 && (
            <div className="mt-4">
              <PaginationControls
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                totalItems={pagination.total}
                pageSize={pagination.pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[10, 20, 50, 100]}
              />
            </div>
          )}
          </Extended>
        </>
      )}

      {/* Feature 116: Entity Detail Modal (existing functional component — unchanged) */}
      <WorkspaceModal
        open={isModalOpen}
        workspaceId={selectedWorkspaceId}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedWorkspaceId(null);
        }}
        onSaved={() => {
          fetchWorkspaces();
        }}
      />

      {/* Workspace Form Modal (existing functional component — unchanged) */}
      {showForm && (
        <WorkspaceForm
          workspace={selectedWorkspace}
          onClose={handleFormClose}
          onSaved={handleWorkspaceSaved}
        />
      )}
    </PageLayout>
  );
};

/** Icon tile background for the Workspace name cell (accent vs neutral). */
function iconTileClass(enabled: boolean): string {
  return (
    'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded ' +
    (enabled ? 'bg-[var(--color-accent)]/10' : 'bg-[var(--color-bg-subtle)]')
  );
}

export default Workspaces;
